import { Overlay } from '@angular/cdk/overlay';
import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  NgZone,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import {
    MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
    MatAutocomplete,
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations/public-api';
import { catchError, debounceTime, distinctUntilChanged, filter, map, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { SearchService } from './search.service';
import { QuickShortcut, SearchResultItemUI, SearchResultSet } from './search.types';

@Component({
    selector: 'search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    exportAs: 'fuseSearch',
    animations: fuseAnimations,
    imports: [
        NgIf,
        MatButtonModule,
        MatIconModule,
        FormsModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        MatOptionModule,
        NgFor,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        NgClass
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
            useFactory: () => {
                const overlay = inject(Overlay);
                return () => overlay.scrollStrategies.reposition();
            },
        },
    ]
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy {
    @Input() appearance: 'basic' | 'bar' = 'basic';
    @Input() debounce: number = 300;
    @Input() minLength: number = 2;
    @Input() limit: number = 5;
    @Output() search: EventEmitter<any> = new EventEmitter<any>();

    opened: boolean = false;
    isLoading: boolean = false;
    resultSets: SearchResultSet[] | null = null;
    partial: boolean = false;
    failedSources: string[] = [];
    selectedCategory: string = 'all';
    searchControl: UntypedFormControl = new UntypedFormControl();
    private _barSearchInputElement: ElementRef;
    private _matAutocomplete: MatAutocomplete;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    // Touch & tap tracking for reliable mobile selection
    private _touchStartX: number = 0;
    private _touchStartY: number = 0;
    private _isSwiping: boolean = false;
    private _lastSelectTime: number = 0;

    quickShortcuts: QuickShortcut[] = [
        {
            title: 'Customers',
            subtitle: 'Manage client accounts',
            icon: 'heroicons_solid:user',
            bgClass: 'bg-blue-50 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400',
            link: ['/trip-mgmt/customers']
        },
        {
            title: 'Drivers',
            subtitle: 'Fleet & driver records',
            icon: 'heroicons_solid:truck',
            bgClass: 'bg-amber-50 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400',
            link: ['/driver-mgmt/drivers']
        },
        {
            title: 'Trips',
            subtitle: 'All bookings & rides',
            icon: 'heroicons_solid:map-pin',
            bgClass: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400',
            link: ['/trip-mgmt/trips']
        },
        {
            title: 'Wallets',
            subtitle: 'Accounts & balances',
            icon: 'heroicons_solid:wallet',
            bgClass: 'bg-purple-50 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400',
            link: ['/admin/wallet-management']
        },
        {
            title: 'CRM Leads',
            subtitle: 'Prospects & follow-ups',
            icon: 'heroicons_solid:funnel',
            bgClass: 'bg-rose-50 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400',
            link: ['/crm/leads']
        }
    ];

    /**
     * Constructor
     */
    constructor(
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _router: Router,
        private _searchService: SearchService,
        private _ngZone: NgZone
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        return {
            'search-appearance-bar': this.appearance === 'bar',
            'search-appearance-basic': this.appearance === 'basic',
            'search-opened': this.opened,
        };
    }

    /**
     * Setter for bar search input
     *
     * @param value
     */
    @ViewChild('barSearchInput')
    set barSearchInput(value: ElementRef) {
        if (value) {
            this._barSearchInputElement = value;
            setTimeout(() => {
                value.nativeElement.focus();
            });
        }
    }

    /**
     * Setter for mat-autocomplete element reference
     *
     * @param value
     */
    @ViewChild('matAutocomplete')
    set matAutocomplete(value: MatAutocomplete) {
        this._matAutocomplete = value;
    }

    @ViewChild(MatAutocompleteTrigger)
    private _matAutocompleteTrigger: MatAutocompleteTrigger;

    /**
     * Filtered result sets based on selected category tab
     */
    get filteredResultSets(): SearchResultSet[] {
        if (!this.resultSets) {
            return [];
        }
        if (this.selectedCategory === 'all') {
            return this.resultSets;
        }
        return this.resultSets.filter(s => s.id === this.selectedCategory);
    }

    /**
     * Total number of results across all categories
     */
    get totalResultCount(): number {
        if (!this.resultSets) {
            return 0;
        }
        return this.resultSets.reduce((sum, s) => sum + (s.results ? s.results.length : 0), 0);
    }

    /**
     * Category tabs for quick filtering
     */
    get categoryTabs(): { id: string; label: string; count: number }[] {
        if (!this.resultSets || this.resultSets.length === 0) {
            return [];
        }
        const tabs = [{ id: 'all', label: 'All', count: this.totalResultCount }];
        for (const rs of this.resultSets) {
            tabs.push({
                id: rs.id,
                label: rs.label,
                count: rs.results.length
            });
        }
        return tabs;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if ('appearance' in changes) {
            this.close();
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        // Close search bar on navigation
        this._router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                if (this.opened) {
                    this.close();
                }
            });

        // Subscribe to the search field value changes
        this.searchControl.valueChanges
            .pipe(
                debounceTime(this.debounce),
                takeUntil(this._unsubscribeAll),
                map((value) => {
                    const query = typeof value === 'string' ? value.trim() : '';

                    if (!query || query.length < this.minLength) {
                        this.resultSets = null;
                        this.partial = false;
                        this.failedSources = [];
                        this.isLoading = false;
                        this.selectedCategory = 'all';
                        return null;
                    }

                    return query;
                }),
                filter((query): query is string => query !== null && query.length >= this.minLength),
                distinctUntilChanged(),
                tap(() => {
                    this.isLoading = true;
                }),
                switchMap((query: string) => {
                    return this._searchService.search(query, this.limit).pipe(
                        catchError((error) => {
                            console.error('Error during global search:', error);
                            this.resultSets = [];
                            this.isLoading = false;
                            return of(null);
                        })
                    );
                })
            )
            .subscribe((response) => {
                this.isLoading = false;

                if (!response) {
                    return;
                }

                const data = response.data || response;
                this.partial = !!(data?.partial ?? response?.partial);
                this.failedSources = data?.failedSources || response?.failedSources || [];
                this.resultSets = this._searchService.mapToResultSets(data);

                // Execute the event
                this.search.next(response);
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Global keyboard shortcut (Cmd+K / Ctrl+K)
     *
     * @param event
     */
    @HostListener('window:keydown', ['$event'])
    handleGlobalShortcut(event: KeyboardEvent): void {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            if (this.opened) {
                this.close();
            } else {
                this.open();
            }
        }
    }

    /**
     * Display function for autocomplete
     *
     * @param value
     */
    displayFn = (value: any): string => {
        if (!value) {
            return '';
        }
        if (typeof value === 'object') {
            return value.title || '';
        }
        return value;
    };

    /**
     * On keydown of the search input
     *
     * @param event
     */
    onKeydown(event: KeyboardEvent): void {
        if (event.code === 'Escape') {
            if (this.appearance === 'bar' && !this._matAutocomplete?.isOpen) {
                this.close();
            }
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Touch & Mobile Gestures
    // -----------------------------------------------------------------------------------------------------

    /**
     * Touch start handler to detect tap vs swipe
     *
     * @param event
     */
    onTouchStart(event: TouchEvent): void {
        if (event.touches && event.touches.length > 0) {
            this._touchStartX = event.touches[0].clientX;
            this._touchStartY = event.touches[0].clientY;
            this._isSwiping = false;
        }
    }

    /**
     * Touch move handler to detect swipe/scroll movement
     *
     * @param event
     */
    onTouchMove(event: TouchEvent): void {
        if (event.touches && event.touches.length > 0) {
            const deltaX = Math.abs(event.touches[0].clientX - this._touchStartX);
            const deltaY = Math.abs(event.touches[0].clientY - this._touchStartY);
            if (deltaX > 10 || deltaY > 10) {
                this._isSwiping = true;
            }
        }
    }

    /**
     * Touch end handler for search result items (single-tap mobile selection)
     *
     * @param result
     * @param event
     */
    onTouchEnd(result: SearchResultItemUI, event: TouchEvent): void {
        if (this._isSwiping) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.onItemSelect(result, event);
    }

    /**
     * Touch end handler for quick shortcut buttons
     *
     * @param link
     * @param event
     */
    onQuickTouchEnd(link: string[], event: TouchEvent): void {
        if (this._isSwiping) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.navigateTo(link, event);
    }

    /**
     * Touch end handler for category filter tabs
     *
     * @param categoryId
     * @param event
     */
    onTabTouchEnd(categoryId: string, event: TouchEvent): void {
        if (this._isSwiping) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.selectCategory(categoryId, event);
    }

    /**
     * Mouse down handler to prevent focused input blur before click registers
     *
     * @param event
     */
    onMouseDown(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }


    /**
     * Touch handler for close button
     *
     * @param event
     */
    onCloseTouch(event: TouchEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.close();
    }

    /**
     * Touch handler for clear search button
     *
     * @param event
     */
    onClearTouch(event: TouchEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.clearSearch(event);
    }

    /**
     * Item click selection handler (single-click navigation)
     *
     * @param result
     * @param event
     */
    onItemSelect(result: SearchResultItemUI, event?: Event): void {
        const now = Date.now();
        if (now - this._lastSelectTime < 350) {
            return;
        }
        this._lastSelectTime = now;

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (this._barSearchInputElement?.nativeElement) {
            this._barSearchInputElement.nativeElement.blur();
        }

        if (result && result.link) {
            const link = Array.isArray(result.link) ? result.link : [result.link];
            this._ngZone.run(() => {
                this._router.navigate(link).catch((err) => {
                    console.error('Search navigation error:', err);
                });
            });
        }
        this.close();
    }

    /**
     * Option selected event from Angular Material autocomplete
     *
     * @param event
     */
    onOptionSelected(event: MatAutocompleteSelectedEvent): void {
        const item = event.option.value as SearchResultItemUI;
        if (item && item.link) {
            this.onItemSelect(item);
        }
    }

    /**
     * Select a category tab on 1 click
     *
     * @param categoryId
     * @param event
     */
    selectCategory(categoryId: string, event?: Event): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.selectedCategory = categoryId;
    }

    /**
     * Alias for selectCategory
     *
     * @param categoryId
     * @param event
     */
    onTabClick(categoryId: string, event?: Event): void {
        this.selectCategory(categoryId, event);
    }

    /**
     * Navigate to link and close search
     *
     * @param link
     * @param event
     */
    navigateTo(link: string[], event?: Event): void {
        const now = Date.now();
        if (now - this._lastSelectTime < 350) {
            return;
        }
        this._lastSelectTime = now;

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (this._barSearchInputElement?.nativeElement) {
            this._barSearchInputElement.nativeElement.blur();
        }

        this._ngZone.run(() => {
            this._router.navigate(link).catch((err) => {
                console.error('Quick shortcut navigation error:', err);
            });
        });
        this.close();
    }

    /**
     * Clear search input
     *
     * @param event
     */
    clearSearch(event?: Event): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.searchControl.setValue('');
        this.resultSets = null;
        this.selectedCategory = 'all';
        this.partial = false;
        this.failedSources = [];
        if (this._barSearchInputElement?.nativeElement) {
            this._barSearchInputElement.nativeElement.focus();
        }
    }

    /**
     * Dismiss failed sources alert banner
     *
     * @param event
     */
    dismissFailedSources(event?: Event): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.failedSources = [];
        if (this._barSearchInputElement?.nativeElement) {
            this._barSearchInputElement.nativeElement.focus();
        }
    }

    /**
     * Focus search input and open autocomplete panel
     *
     * @param event
     */
    focusSearchInput(event?: Event): void {
        const target = event?.target as HTMLElement;
        if (target && target.closest('button')) {
            return;
        }
        if (this._barSearchInputElement?.nativeElement) {
            this._barSearchInputElement.nativeElement.focus();
        }
        if (this._matAutocompleteTrigger && !this._matAutocompleteTrigger.panelOpen) {
            this._matAutocompleteTrigger.openPanel();
        }
    }

    /**
     * On search input click
     */
    onInputClick(): void {
        if (this._matAutocompleteTrigger && !this._matAutocompleteTrigger.panelOpen) {
            this._matAutocompleteTrigger.openPanel();
        }
    }

    /**
     * On search input focus
     */
    onInputFocus(): void {
        if (this._matAutocompleteTrigger && !this._matAutocompleteTrigger.panelOpen) {
            this._matAutocompleteTrigger.openPanel();
        }
    }

    /**
     * Open the search
     * Used in 'bar'
     */
    open(): void {
        if (this.opened) {
            return;
        }

        this.opened = true;
    }

    /**
     * Close the search
     */
    close(): void {
        if (this._matAutocompleteTrigger?.panelOpen) {
            this._matAutocompleteTrigger.closePanel();
        }

        if (this.appearance === 'basic') {
            this.searchControl.setValue('', { emitEvent: false });
            this.resultSets = null;
            this.selectedCategory = 'all';
            this.partial = false;
            this.failedSources = [];
            this.isLoading = false;
            return;
        }

        if (!this.opened) {
            return;
        }

        this.searchControl.setValue('');
        this.resultSets = null;
        this.selectedCategory = 'all';
        this.partial = false;
        this.failedSources = [];
        this.isLoading = false;
        this.opened = false;
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
}
